import React, { useEffect, useState } from "react";
import { api, fmtCNPJ, fmtRelative, cstatVariant } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowsClockwise,
  Database,
  Files,
  Warning,
  Receipt,
  CheckCircle,
  Clock,
  Hourglass,
  ListChecks as ListChecksIcon,
} from "@phosphor-icons/react";

const Kpi = ({ label, value, hint, icon: Icon, testid, accent }) => (
  <div
    className="bg-white border border-border rounded-sm p-5 h-full"
    data-testid={testid}
  >
    <div className="flex items-center justify-between">
      <div className="label-overline">{label}</div>
      {Icon && (
        <Icon
          size={18}
          weight="regular"
          className={accent ? "text-amber-600" : "text-muted-foreground"}
        />
      )}
    </div>
    <div className="kpi-value mt-2 font-mono">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </div>
);

const fmtCountdown = (s) => {
  if (s == null || s === 0) return "agora";
  if (s >= 999999) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
};

const Dashboard = () => {
  const [kpis, setKpis] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [k, ws] = await Promise.all([
        api.get("/dashboard/kpis"),
        api.get("/dashboard/worker-status"),
      ]);
      setKpis(k.data);
      setTenants(ws.data.tenants || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="page-dashboard">
      <PageHeader
        title="Dashboard"
        subtitle="Visão geral do monitoramento. O sistema gerencia as consultas à SEFAZ de forma inteligente para garantir que as notas cheguem sempre no menor tempo possível."
        testid="dashboard-header"
        actions={
          <button
            onClick={load}
            data-testid="dashboard-refresh-btn"
            className="inline-flex items-center gap-1.5 text-sm rounded-sm border border-border px-3 py-1.5 hover:bg-muted transition-colors"
          >
            <ArrowsClockwise size={14} /> Atualizar
          </button>
        }
      />

      <div className="px-8 py-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {loading || !kpis ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-28 rounded-sm" />
            ))
          ) : (
            <>
              <Kpi
                testid="kpi-tenants"
                label="Empresas ativas"
                value={`${kpis.tenants_ativos}/${kpis.total_tenants}`}
                hint="CNPJs sendo monitorados"
                icon={Database}
              />
              <Kpi
                testid="kpi-total-nfe"
                label="Total de Notas"
                value={kpis.total_nfe}
                hint={`${kpis.nfe_24h} capturadas nas últimas 24h`}
                icon={Files}
              />
              <Kpi
                testid="kpi-modelo55"
                label="NF-e / NFC-e"
                value={`${kpis.nfe_modelo55} / ${kpis.nfe_modelo65}`}
                hint="Separação por modelo"
                icon={Receipt}
              />
              <Kpi
                testid="kpi-entradas"
                label="Notas de Entrada"
                value={kpis.nfe_entrada}
                hint="Emitidas contra seus CNPJs"
                icon={Receipt}
              />
              <Kpi
                testid="kpi-manifestadas"
                label="Manifestadas"
                value={kpis.manifestadas}
                hint="Ciência da operação concluída"
                icon={CheckCircle}
              />
              <Kpi
                testid="kpi-pendentes"
                label="Pendentes de Manifestação"
                value={kpis.pendentes_manifestacao}
                hint="Aguardando processamento"
                icon={Warning}
              />
              <Kpi
                testid="kpi-eventos"
                label="Total de Eventos"
                value={kpis.eventos_total}
                hint={`${kpis.eventos_erro} retornaram com erro`}
                icon={ListChecksIcon}
              />
              <Kpi
                testid="kpi-logs-24h"
                label="Sincronizações (24h)"
                value={kpis.logs_24h}
                hint="Consultas realizadas na SEFAZ"
                icon={ArrowsClockwise}
              />
            </>
          )}
        </div>

        {kpis && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi
              testid="kpi-em-fila"
              label="Fila de Sincronização"
              value={kpis.em_fila}
              hint="Empresas aguardando consulta"
              icon={Hourglass}
            />
            <Kpi
              testid="kpi-cooldown-137"
              label="Em Pausa Regulamentar"
              value={kpis.em_cooldown_137}
              hint="Aguardando 1h (Sem novas notas)"
              icon={Clock}
            />
            <Kpi
              testid="kpi-retry-backoff"
              label="Falhas de Conexão"
              value={kpis.em_retry_backoff}
              hint="Tentando reconectar à SEFAZ"
              icon={Warning}
              accent={kpis.em_retry_backoff > 0}
            />
            <Kpi
              testid="kpi-consumo-indevido"
              label="Alertas de Consumo"
              value={kpis.consumo_indevido_total}
              hint="Bloqueios por excesso de consultas"
              icon={Warning}
              accent={kpis.consumo_indevido_total > 0}
            />
          </div>
        )}

        <div className="bg-white border border-border rounded-sm">
          <div className="px-5 py-4 border-b border-border flex flex-col lg:flex-row gap-2 items-center justify-between">
            <div>
              <h2 className="text-base font-semibold tracking-tight">
                Status do Worker por CNPJ
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Próxima execução é controlada pela regra: 1 hora após cStat 137
                / 10 minutos em outros casos. Erros disparam backoff
                exponencial.
              </p>
            </div>
            <StatusBadge variant="info" testid="worker-status-running">
              Agendador ativo
            </StatusBadge>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table" data-testid="worker-status-table">
              <thead>
                <tr>
                  <th>Razão Social</th>
                  <th>CNPJ</th>
                  <th>Ambiente</th>
                  <th>Último NSU</th>
                  <th>cStat</th>
                  <th>Último Sync</th>
                  <th>Cooldown</th>
                  <th>Retry</th>
                  <th>Documentos</th>
                </tr>
              </thead>
              <tbody>
                {tenants.length === 0 && (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center text-muted-foreground py-8"
                    >
                      Nenhuma empresa cadastrada.
                    </td>
                  </tr>
                )}
                {tenants.map((t) => (
                  <tr key={t.id} data-testid={`worker-row-${t.id}`}>
                    <td>
                      <div className="font-medium">{t.razao_social}</div>
                      <div className="text-xs text-muted-foreground">
                        {t.uf}
                      </div>
                    </td>
                    <td className="font-mono">{fmtCNPJ(t.cnpj)}</td>
                    <td>
                      <StatusBadge
                        variant={
                          t.ambiente === "producao"
                            ? "success"
                            : t.ambiente === "homologacao"
                              ? "warning"
                              : "info"
                        }
                      >
                        {t.ambiente === "producao"
                          ? "Produção"
                          : t.ambiente === "homologacao"
                            ? "Homologação"
                            : "Mockado"}
                      </StatusBadge>
                    </td>
                    <td className="font-mono text-xs">{t.ult_nsu}</td>
                    <td>
                      {t.last_cstat ? (
                        <StatusBadge variant={cstatVariant(t.last_cstat)}>
                          cStat {t.last_cstat}
                        </StatusBadge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="text-xs">{fmtRelative(t.ultimo_sync)}</td>
                    <td>
                      {t.eligible ? (
                        <StatusBadge variant="success">pronto</StatusBadge>
                      ) : (
                        <span className="font-mono text-xs">
                          {fmtCountdown(t.cooldown_seconds)}
                        </span>
                      )}
                    </td>
                    <td>
                      {(t.retry_count || 0) > 0 ? (
                        <StatusBadge variant="warning" testid={`retry-${t.id}`}>
                          {t.retry_count}x
                        </StatusBadge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </td>
                    <td className="font-mono">{t.docs_capturados || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
